import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, CircleDashed, CircleHalf, CheckCircle, ListChecks } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Explainer, WarmEmptyState } from "../components/friendly";

const STATUS = {
  open: { label: "Open", icon: CircleDashed, cls: "text-slate-500 bg-slate-100", next: "in_progress" },
  in_progress: { label: "In progress", icon: CircleHalf, cls: "text-amber-700 bg-amber-50", next: "done" },
  done: { label: "Done", icon: CheckCircle, cls: "text-emerald-700 bg-emerald-50", next: "open" },
};

export default function Tasks() {
  const { activeClientId, activeClient, clients } = useApp();
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", assignee_id: "", due_date: "", note: "" });

  const load = useCallback(() => {
    if (activeClientId) api.get(`/tasks?client_id=${activeClientId}`).then((r) => setTasks(r.data));
    api.get("/team").then((r) => setTeam(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Task title required");
    await api.post("/tasks", { ...form, client_id: activeClientId });
    setOpen(false);
    setForm({ title: "", assignee_id: "", due_date: "", note: "" });
    load();
    toast.success("Task routed");
  };

  const cycle = async (t) => {
    await api.put(`/tasks/${t.task_id}`, { status: STATUS[t.status].next });
    load();
  };

  const remove = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Routing Tasks</h1>
          <p className="text-sm text-slate-500 mt-1">Hand work between roles for <b>{activeClient?.name}</b> — design, translation, review.</p>
        </div>
        <Button data-testid="new-task-button" onClick={() => setOpen(true)} className="rounded-lg gap-2 bg-slate-900 hover:bg-slate-700"><Plus size={14} /> New task</Button>
      </div>
      <Explainer id="tasks">Hand work between teammates without leaving the app — assign a design tweak, a translation, or a review, then track it from open to done.</Explainer>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float divide-y divide-slate-100">
        {tasks.map((t) => {
          const S = STATUS[t.status] || STATUS.open;
          return (
            <div key={t.task_id} data-testid={`task-row-${t.task_id}`} className="px-5 py-3.5 flex items-center gap-3">
              <button data-testid={`task-status-${t.task_id}`} onClick={() => cycle(t)} title={`Mark ${STATUS[S.next]?.label || S.next}`}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${S.cls} hover:opacity-80 transition-opacity shrink-0`}>
                <S.icon size={13} weight={t.status === "done" ? "fill" : "regular"} /> {S.label}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-medium truncate ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</p>
                <p className="text-[11px] text-slate-400">
                  {t.assignee_name ? `→ ${t.assignee_name}` : "Unassigned"} {t.due_date && `· due ${t.due_date}`} · by {t.created_by}
                </p>
              </div>
              <button data-testid={`task-delete-${t.task_id}`} onClick={() => remove(t.task_id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash size={14} /></button>
            </div>
          );
        })}
        {!tasks.length && <div className="p-4"><WarmEmptyState icon={ListChecks} title="No tasks for this brand" what="Use tasks to hand work to a teammate — a design tweak, a translation, a review — and track it from open to done. Create one with the button above." /></div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Route a task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="task-title-input" placeholder="e.g. Translate June captions to Spanish" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
              <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent>
                {team.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name} · {m.role}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="task-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            <Button data-testid="task-create-button" onClick={create} className="w-full rounded-lg bg-slate-900 hover:bg-slate-700">Create task</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
